import { useEffect, useRef } from 'react';

import { type NoteEditorVariant } from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import {
  addDatadogRumAction,
  getDatadogClientStatus,
  setDatadogEditorContext,
} from '@/utils/tracing';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { type Editor } from '@tiptap/core';

export const EDITOR_LIFECYCLE_ACTION = 'editor_lifecycle';
export const DESTROYED_EDITOR_ACCESS_ACTION = 'destroyed_editor_access';
export const EDITOR_TEARDOWN_ACTION = 'editor_teardown';

/**
 * Editor surfaces that report lifecycle telemetry. `note` / `note_offscreen`
 * additionally define note-tab SLI membership; `document` and `template` are
 * lifecycle-only (RUM separates them by `@view.url`).
 */
export type EditorTelemetrySurface = 'note' | 'note_offscreen' | 'document' | 'template';

export type EditorTeardownReason =
  | 'route_changed'
  | 'flags_unresolved'
  | 'parent_withheld'
  | 'pane_unmounted';

export type EditorLifecycleAction = 'create' | 'destroy';

/** Distinct destroyed-access call sites reported per page load. */
const MAX_DESTROYED_ACCESS_SITES = 20;

/** Create+destroy lifecycle events reported per page load. */
const MAX_LIFECYCLE_EVENTS = 40;

// The counters above and below are per JS lifetime — on desktop, a whole
// session. Every tab switch unmounts a pane, and hosts defer to the pane
// unconditionally, so a starved `pane_unmounted` hides those teardowns whole.
const TEARDOWN_CAPS: Record<EditorTeardownReason, number> = {
  flags_unresolved: 20,
  parent_withheld: 20,
  route_changed: 10,
  pane_unmounted: 20,
};

const reportedDestroyedSites = new Set<string>();
let lifecycleEventsEmitted = 0;
const teardownEmitted: Record<EditorTeardownReason, number> = {
  flags_unresolved: 0,
  parent_withheld: 0,
  route_changed: 0,
  pane_unmounted: 0,
};

const mountedCounts = new Map<string, number>();

const mountedKey = (
  variant: NoteEditorVariant,
  surface: EditorTelemetrySurface
): string => `${variant}|${surface}`;

const rumReady = (): boolean => getDatadogClientStatus().rumInitialized;

/**
 * Reports post-destroy access from `isEditorLive` / `whenEditorLive`. Null
 * editors are not access and stay silent. Repeats of the same `call_site` are
 * dropped so a rAF loop cannot flood RUM; the first 20 distinct sites per
 * page load get through.
 */
export function reportDestroyedEditorAccess(callSite: string): void {
  try {
    if (!rumReady()) return;
    if (reportedDestroyedSites.size >= MAX_DESTROYED_ACCESS_SITES) return;
    if (reportedDestroyedSites.has(callSite)) return;
    reportedDestroyedSites.add(callSite);
    addDatadogRumAction(DESTROYED_EDITOR_ACCESS_ACTION, { call_site: callSite });
  } catch {
    // A diagnostic must never destabilise the page it observes.
  }
}

export function reportEditorLifecycle({
  variant,
  surface,
  action,
}: {
  variant: NoteEditorVariant;
  surface: EditorTelemetrySurface;
  action: EditorLifecycleAction;
}): void {
  try {
    const key = mountedKey(variant, surface);
    const current = mountedCounts.get(key) ?? 0;
    const next = action === 'create' ? current + 1 : Math.max(0, current - 1);
    mountedCounts.set(key, next);

    if (!rumReady()) return;
    if (lifecycleEventsEmitted >= MAX_LIFECYCLE_EVENTS) return;
    lifecycleEventsEmitted += 1;
    addDatadogRumAction(EDITOR_LIFECYCLE_ACTION, {
      variant,
      surface,
      action,
      mounted_count: next,
    });
  } catch {
    // A diagnostic must never destabilise the page it observes.
  }
}

export function reportEditorTeardown({
  variant,
  surface,
  reason,
}: {
  variant: NoteEditorVariant;
  surface: 'note' | 'document';
  reason: EditorTeardownReason;
}): void {
  try {
    if (!rumReady()) return;
    if (teardownEmitted[reason] >= TEARDOWN_CAPS[reason]) return;
    teardownEmitted[reason] += 1;
    addDatadogRumAction(EDITOR_TEARDOWN_ACTION, { variant, surface, reason });
  } catch {
    // A diagnostic must never destabilise the page it observes.
  }
}

/**
 * Emits `editor_lifecycle` for one TipTap instance. Create on a live editor
 * appearing; destroy on the effect cleanup (instance gone or hook unmounted).
 */
export function useEditorLifecycleTelemetry({
  editor,
  variant,
  surface,
}: {
  editor: Editor | null | undefined;
  variant: NoteEditorVariant;
  surface: EditorTelemetrySurface;
}): void {
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    reportEditorLifecycle({ variant, surface, action: 'create' });
    return () => {
      reportEditorLifecycle({ variant, surface, action: 'destroy' });
    };
  }, [editor, variant, surface]);
}

/**
 * Stamps the routed variant onto Datadog global context (and the logger
 * enrich store). Clearing on unmount / `null` drops a stale stamp so a
 * transcript-only view is not attributed to the previous editor.
 */
export function useDatadogEditorContext(variant: NoteEditorVariant | null): void {
  useEffect(() => {
    if (variant === null) return undefined;
    setDatadogEditorContext(variant);
    return () => setDatadogEditorContext(null);
  }, [variant]);
}

/** Logs the read-only whole-note view flipping back to an editor while the
 * pane is still mounted. `isActive` is the flag-and-surface value (stable
 * across note-state changes like a regeneration flipping the note to
 * IN_PROGRESS), not the note-state-gated view value; and unmount itself fires
 * on every tab switch — the in-pane transition is the signal that a flip
 * back actually happened (a release-flag flip or a surface change, not
 * navigation and not a regenerate).
 *
 * `hasRendered` latches: without it, a Hyperspace tab where the view never
 * rendered (a markdown note, an empty note) still logs the flip when the
 * flag or the surface goes false, inflating the one signal this log exists
 * for. The log only fires once the view actually rendered in the current
 * active period. The context object is ids-and-enums only — session and
 * EHR provider, never note content — so a Datadog query can answer which
 * session and which cohort flipped. */
export function useReadonlyWholeNoteViewObservability(
  isActive: boolean,
  hasRendered: boolean,
  context?: { sessionId: string; ehrProvider?: EHRProvider | null }
): void {
  const hasRenderedInPeriodRef = useRef(false);
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (isActive) {
      // A fresh active period must render again before it can log a flip.
      hasRenderedInPeriodRef.current = false;
    }
    if (hasRendered) {
      hasRenderedInPeriodRef.current = true;
    }
    if (wasActiveRef.current && !isActive && hasRenderedInPeriodRef.current) {
      logger.info('Read-only whole-note view flipped back to an editor', {
        session_id: context?.sessionId,
        ehr_provider: context?.ehrProvider ?? undefined,
      });
    }
    wasActiveRef.current = isActive;
  }, [isActive, hasRendered, context?.sessionId, context?.ehrProvider]);
}

/** Test-only: reset caps and mounted counts between cases. */
export function __resetEditorObservabilityForTests(): void {
  reportedDestroyedSites.clear();
  lifecycleEventsEmitted = 0;
  for (const reason of Object.keys(teardownEmitted) as EditorTeardownReason[]) {
    teardownEmitted[reason] = 0;
  }
  mountedCounts.clear();
}
